<template>
    <div>
        <ClTableFilterable
            :headers="headers"
            :hideAdd="true"
            :filter.sync="tableFilter"
            :items="resultListMapped"
            :itemsPerPage="10"
            :loading="loading"
            loadingText="Searching"
            class="elevation-2"
            @click:row="rowClickHandler( $event )"
        >
            <template #[`item.reprint`]="{ item }">
                <ClButton icon @click.stop.prevent="reprintLabelButtonHandler(item)">
                    <v-icon>{{ item.reprint }}</v-icon>
                </ClButton>
            </template>
            <template #[`item.void`]="{ item }">
                <ModalVoid
                    v-if="item.status === 'Open'"
                    :mailItemId="item.id"
                    @voidItemSuccess="id => $store.dispatch( 'Mail/Search/setResultAsVoid', { resultId: id } )"
                />
            </template>
        </ClTableFilterable>
        <ClButton
            class="export-button"
            color="primary"
            size="large"
            :rounded="false"
            :loading="exportInfo.inFlight"
            :disabled="( !hasResultList || exportInfo.inFlight )"
            @click="exportButtonHandler()"
        >
            Export
        </ClButton>
    </div>
</template>

<script>
// Vuex
import { mapGetters, mapState } from 'vuex';

// Packages
import _ from 'lodash';

// Api
import LogisticsApi from '@/api/LogisticsApi';

// Services
import CsvService from '@/services/CsvService';
import LabelPrintingService from '@/services/LabelPrintingService';

// Utils
import DateUtil from '@/utils/DateUtil';
import ErrorUtil from '@/utils/ErrorUtil';

// Components
import ModalVoid from '@/views/mail/search/ModalVoid';

export default {
    name: 'CustomerSearchTable',
    components: {
        ModalVoid,
    },
    props: {
        resultList: {
            type: Array,
            required: true,
        },
        loading: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            exportInfo: {
                inFlight: false,
            },
            reprintLabelInfo: {
                inFlight: false,
                // mailItemIdSet is used so we can display the proper loading state on each in-flight item's button
                mailItemIdSet: ( new Set() ),
            },
            showModalPng: false,
            labelFileName: '',
            labelErrorMessage: '',
            headers: [
                { text: 'Tracking #', value: 'trackingNumber' },
                { text: 'Recipient', value: 'customerFullName' },
                { text: 'Sender', value: 'sender' },
                { text: 'Carrier', value: 'mailCarrierFriendly' },
                { text: 'Date Received', value: 'dateReceived' },
                { text: 'Ref #', value: 'refNum' },
                { text: 'Status', value: 'status' },
                { text: 'Reprint', value: 'reprint', align: 'center' },
                { text: 'Void', value: 'void', align: 'center' },
            ],
            tableFilter: '',
        };
    },
    computed: {
        resultListMapped() {
            return this.resultList.map(
                ( customer ) => ( {
                    ...customer,
                    reprint: 'mdi-printer',
                    void: 'mdi-cancel',
                } ),
            );
        },
        ...mapGetters( {
            websiteId: 'Website/id',
        } ),
        ...mapState( {
            jwtToken: ( state ) => state.User.jwtToken,
            userAccountId: ( state ) => state.User.accountId,
            searchFilters: ( state ) => state.Mail.Search.filters,
            searchInFlight: ( state ) => state.Mail.Search.results.inFlight,
        } ),

        /** @returns { Boolean } */
        hasResultList() {
            return !!this.resultList
                && !!this.resultList.length
                && ( this.resultList.length > 0 );
        },
    },
    methods: {
        rowClickHandler( { id } ) {
            this.$router.push( {
                name: 'MailSearchDetail',
                params: { id },
            } );
        },
        displayNoReprintModal() {
            this.showModalPng = true;
        },
        hideNoReprintModal() {
            this.showModalPng = false;
        },
        async exportButtonHandler() {
            this.exportInfo.inFlight = true;
            try {
                const exportParameters = {
                    jwtToken: this.jwtToken,
                    websiteId: this.websiteId,
                    userAccountId: this.userAccountId,
                    customerId: this.searchFilters?.customer?.customerId,
                    sender: this.searchFilters?.sender,
                    trackingNumber: this.searchFilters?.trackingNumber,
                    mailTypeId: this.searchFilters?.mailType?.id,
                    receivedDateStart: DateUtil.formatDate( this.searchFilters?.dateRange?.start ),
                    receivedDateEnd: DateUtil.formatDate( this.searchFilters?.dateRange?.end ),
                };

                // Filters out keys from the object that have falsy values
                // https://stackoverflow.com/a/26295351
                // (Replace .pick with .pickBy in answer due to updates in lodash)
                const filteredExportParameters = _.pickBy( exportParameters, _.identity );

                const { data } = await LogisticsApi.getSearchResultsExport( filteredExportParameters );
                CsvService.exportDataAsCsv( data, 'SearchExport' );
            } catch ( error ) {
                this.$store.dispatch( 'snackbar', {
                    message: `Error: ${ ErrorUtil.getMessageFromError( error ) }`,
                    type: 'error',
                    duration: 5000,
                } );
            } finally {
                this.exportInfo.inFlight = false;
            }
        },

        async reprintLabel( mailItemId ) {
            this.reprintLabelInfo.inFlight = true;
            this.reprintLabelInfo.mailItemIdSet.add( mailItemId );
            try {
                const { data } = await LogisticsApi.printMailLabel( {
                    jwtToken: this.jwtToken,
                    websiteId: this.websiteId,
                    userAccountId: this.userAccountId,
                    mailItemId,
                } );
                this.labelFileName = data.fileName;
                if ( data.fileName ) {
                    await LabelPrintingService.printLabel( data.fileName );
                } else {
                    throw new Error( 'Unable to fetch label to reprint.' );
                }
            } catch ( error ) {
                this.labelErrorMessage = error?.response?.data?.message || error.message;
                this.displayNoReprintModal();
            } finally {
                this.reprintLabelInfo.inFlight = false;
                this.reprintLabelInfo.mailItemIdSet.delete( mailItemId );
            }
        },
        reprintLabelButtonHandler( mailItem ) {
            this.reprintLabel( mailItem.id );
        },
        closeReprint() {
            this.showModalPng = false;
        },
    },
};
</script>

<style scoped>
    .export-button {
        margin-top: 2rem;
    }

    ::v-deep.v-data-table {
        min-width: 100%;
    }
</style>
